import type { DeviceSession, SessionData } from '../model/types';

const getMobileDeviceName = (ua: string): string | null => {
    if (ua.includes('iphone')) return 'iPhone';

    if (ua.includes('ipad')) return 'iPad';

    if (ua.includes('android')) {
        if (ua.includes('mobile')) return 'Android Phone';

        if (ua.includes('tablet')) return 'Android Tablet';

        return 'Android Device';
    }

    return null;
};

const getDesktopDeviceName = (ua: string): string | null => {
    if (ua.includes('windows')) return 'Windows PC';

    if (ua.includes('macintosh') || ua.includes('mac os')) return 'Mac';

    if (ua.includes('linux')) return 'Linux PC';

    return null;
};

const getBrowserName = (ua: string): string | null => {
    if (ua.includes('chrome')) return 'Chrome Browser';

    if (ua.includes('firefox')) return 'Firefox Browser';

    if (ua.includes('safari') && !ua.includes('chrome'))
        return 'Safari Browser';

    if (ua.includes('edge')) return 'Edge Browser';

    return null;
};

const parseDeviceName = (userAgent: string): string => {
    const ua = userAgent.toLowerCase();

    const mobile = getMobileDeviceName(ua);

    if (mobile) return mobile;

    const desktop = getDesktopDeviceName(ua);

    if (desktop) return desktop;

    const browser = getBrowserName(ua);

    if (browser) return browser;

    const parts = userAgent.split(' ');
    const firstPart = parts[0]?.split('/')[0];

    return firstPart || 'Unknown Device';
};

const getDeviceType = (userAgent: string): string => {
    const ua = userAgent.toLowerCase();

    if (
        ua.includes('mobile') ||
        ua.includes('iphone') ||
        ua.includes('android')
    ) {
        return 'Mobile';
    }

    if (ua.includes('tablet') || ua.includes('ipad')) {
        return 'Tablet';
    }

    if (
        ua.includes('windows') ||
        ua.includes('macintosh') ||
        ua.includes('linux')
    ) {
        return 'Desktop';
    }

    return 'Device';
};

const parseUserAgent = (userAgent?: string) => {
    if (!userAgent) {
        return { name: 'Unknown Device', type: 'Device' };
    }

    const name = parseDeviceName(userAgent);
    const type = getDeviceType(userAgent);

    return { name, type };
};

export const transformSessionToDevice = (
    session: SessionData,
    currentJti?: string,
): DeviceSession => {
    const { name, type } = parseUserAgent(session.user_agent);

    return {
        id: session.jti,
        name,
        type,
        location: session.location || 'Unknown Location',
        lastUsed: session.last_seen,
        isCurrent: session.jti === currentJti,
    };
};
